/**
 * LegalDocument — shared layout for Privacy Policy and Terms & Conditions.
 *
 * Reuses the live Navbar + Footer and the same typographic system as the
 * rest of the site. Renders a clean two-column page on desktop (sticky
 * section index on the left, body on the right) and stacks them on mobile.
 *
 * Pass:
 *   - eyebrow:   "Legal" or similar small uppercase label
 *   - title:     H1 (e.g. "Privacy Policy")
 *   - updated:   "28 June 2026" — appears under the H1
 *   - sections:  [{ id, heading, body }]  ← `body` is a React node
 *   - intro:     short lead paragraph(s) — React node
 */
import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

export default function LegalDocument({
    eyebrow = "Legal",
    title,
    updated,
    intro,
    sections,
    testIdPrefix = "legal",
}) {
    const [activeId, setActiveId] = useState(sections[0]?.id);

    useEffect(() => {
        const handler = () => {
            const offsets = sections
                .map((s) => {
                    const el = document.getElementById(s.id);
                    if (!el) return null;
                    return { id: s.id, top: el.getBoundingClientRect().top };
                })
                .filter(Boolean);
            const visible = offsets.find((o) => o.top > 80);
            const current = visible
                ? offsets[Math.max(0, offsets.indexOf(visible) - 1)]
                : offsets[offsets.length - 1];
            if (current) setActiveId(current.id);
        };
        window.addEventListener("scroll", handler, { passive: true });
        handler();
        return () => window.removeEventListener("scroll", handler);
    }, [sections]);

    return (
        <div data-testid={`${testIdPrefix}-page`} className="min-h-screen bg-white">
            <Navbar />

            {/* Page header */}
            <header className="bg-white border-b border-srm-line">
                <div className="container-srm py-16 lg:py-24">
                    <nav
                        aria-label="Breadcrumb"
                        className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-srm-body/70 font-display"
                    >
                        <a href="/" className="hover:text-srm-gold transition-colors">
                            Home
                        </a>
                        <ChevronRight className="h-3 w-3" strokeWidth={2} />
                        <span className="text-srm-navy/70">{eyebrow}</span>
                        <ChevronRight className="h-3 w-3" strokeWidth={2} />
                        <span className="text-srm-navy">{title}</span>
                    </nav>

                    <div className="mt-7">
                        <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[12px]">
                            {eyebrow}
                        </div>
                        <span aria-hidden className="mt-5 block h-[3px] w-16 bg-srm-gold" />
                        <h1
                            data-testid={`${testIdPrefix}-title`}
                            className="mt-7 font-display font-bold text-[2.1rem] sm:text-[2.5rem] lg:text-[3rem] tracking-[-0.022em] leading-[1.1] text-srm-navy text-balance"
                        >
                            {title}
                        </h1>
                        <p
                            data-testid={`${testIdPrefix}-updated`}
                            className="mt-5 text-[13.5px] tracking-[0.18em] uppercase text-srm-body/70 font-display"
                        >
                            Last updated · {updated}
                        </p>
                    </div>
                </div>
            </header>

            {/* Body — two column on desktop, stacked on mobile */}
            <div className="bg-white">
                <div className="container-srm py-16 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                        {/* Sticky section index — desktop only */}
                        <aside className="hidden lg:block lg:col-span-3">
                            <div className="sticky top-32">
                                <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[11px]">
                                    On this page
                                </div>
                                <span
                                    aria-hidden
                                    className="mt-4 block h-[2px] w-12 bg-srm-gold"
                                />
                                <ol className="mt-6 space-y-3">
                                    {sections.map((s, i) => (
                                        <li key={s.id}>
                                            <a
                                                href={`#${s.id}`}
                                                data-testid={`${testIdPrefix}-toc-${s.id}`}
                                                className={`block text-[13.5px] leading-[1.55] transition-colors ${
                                                    activeId === s.id
                                                        ? "text-srm-navy font-semibold"
                                                        : "text-srm-body hover:text-srm-navy"
                                                }`}
                                            >
                                                <span className="text-srm-muted mr-2 font-display text-[11.5px]">
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                {s.heading}
                                            </a>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </aside>

                        {/* Body column */}
                        <article className="lg:col-span-9 max-w-3xl">
                            {/* Intro */}
                            <div
                                data-testid={`${testIdPrefix}-intro`}
                                className="text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.8] space-y-4"
                            >
                                {intro}
                            </div>

                            {/* Sections */}
                            <div className="mt-12 lg:mt-16 space-y-12 lg:space-y-16">
                                {sections.map((s, i) => (
                                    <section
                                        key={s.id}
                                        id={s.id}
                                        data-testid={`${testIdPrefix}-section-${s.id}`}
                                        className="scroll-mt-32"
                                    >
                                        <div className="flex items-baseline gap-4">
                                            <span className="font-display font-bold text-srm-gold text-[13px] tracking-[0.18em]">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <h2 className="font-display font-bold text-srm-navy text-[1.35rem] lg:text-[1.55rem] tracking-[-0.015em] leading-[1.2]">
                                                {s.heading}
                                            </h2>
                                        </div>
                                        <div className="mt-5 text-[15.5px] lg:text-[16px] text-srm-body leading-[1.8] space-y-4">
                                            {s.body}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        </article>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
